import React, { useState, useMemo } from 'react'

import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TablePagination,
} from '@material-ui/core'
import {
    People as PeopleIcon,
    Search as SearchIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
} from '@material-ui/icons'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'

import { CampaignProspect } from '../../../../models/Outreach/types'
import { CampaignEmailUnsubscribeIndicator } from '../../CampaignEmailUnsubscribeIndicator'

const useStyles = makeStyles((theme: Theme) => ({
    activityCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    tableContainer: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    table: {
        minWidth: 650,
    },
    tableHead: {
        backgroundColor: '#f8fafc',
        '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#1f2937',
            fontSize: '0.875rem',
        },
    },
    tableRow: {
        '&:hover': {
            backgroundColor: 'rgba(248, 250, 252, 0.5)',
        },
    },
    statusChip: {
        fontSize: '0.75rem',
        height: 24,
    },
    tabContent: {
        width: '100%',
        paddingLeft: `calc(${theme.spacing(3)} + 24px)`,
        paddingRight: `calc(${theme.spacing(3)} + 24px)`,
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    searchField: {
        flex: 1,
        minWidth: 250,
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
        },
    },
    filterControl: {
        minWidth: 150,
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
        },
    },
    sortableHeader: {
        cursor: 'pointer',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
    },
    sortIcon: {
        fontSize: 16,
        color: '#6b7280',
    },
}))

interface ProspectTrackingTabProps {
    prospects: CampaignProspect[]
    isLoading: boolean
    error: Error | null | unknown
}

type SortField =
    | 'name'
    | 'status'
    | 'current_step'
    | 'messages_sent'
    | 'messages_opened'
    | 'messages_clicked'
    | 'started_at'
    | 'last_step_sent_at'
    | 'next_step_scheduled_at'

type SortDirection = 'asc' | 'desc'

export const ProspectTrackingTab: React.FC<ProspectTrackingTabProps> = ({
    prospects,
    isLoading,
    error,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [sortField, setSortField] = useState<SortField | null>(null)
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    const getStatusColor = (status: string) => {
        switch (status.toUpperCase()) {
            case 'ACTIVE':
                return '#f59e0b' // yellow/amber
            case 'CONVERTED':
                return '#10b981' // green
            case 'COMPLETED':
                return 'default'
            case 'STOPPED':
                return 'secondary'
            case 'REPLIED':
                return 'default'
            case 'PENDING':
                return 'default'
            default:
                return 'default'
        }
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-'
        try {
            const date = new Date(dateString)
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            })
        } catch {
            return dateString
        }
    }

    // Get unique statuses for filter
    const uniqueStatuses = useMemo(() => {
        const statuses = new Set<string>()
        prospects.forEach((prospect) => {
            statuses.add(prospect.status.toUpperCase())
        })
        return Array.from(statuses).sort()
    }, [prospects])

    // Filter and sort prospects
    const filteredAndSortedProspects = useMemo(() => {
        let filtered = prospects.filter((prospect) => {
            // Search filter
            const matchesSearch =
                searchQuery === '' ||
                prospect.prospect.name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())

            // Status filter
            const matchesStatus =
                statusFilter === 'all' ||
                prospect.status.toUpperCase() === statusFilter.toUpperCase()

            return matchesSearch && matchesStatus
        })

        // Sort
        if (sortField) {
            filtered = [...filtered].sort((a, b) => {
                let aValue: any
                let bValue: any

                switch (sortField) {
                    case 'name':
                        aValue = a.prospect.name.toLowerCase()
                        bValue = b.prospect.name.toLowerCase()
                        break
                    case 'status':
                        aValue = a.status.toUpperCase()
                        bValue = b.status.toUpperCase()
                        break
                    case 'current_step':
                        aValue = a.current_step?.step_order || 0
                        bValue = b.current_step?.step_order || 0
                        break
                    case 'messages_sent':
                        aValue = a.messages_sent
                        bValue = b.messages_sent
                        break
                    case 'messages_opened':
                        aValue = a.messages_opened
                        bValue = b.messages_opened
                        break
                    case 'messages_clicked':
                        aValue = a.messages_clicked
                        bValue = b.messages_clicked
                        break
                    case 'started_at':
                        aValue = a.started_at
                            ? new Date(a.started_at).getTime()
                            : 0
                        bValue = b.started_at
                            ? new Date(b.started_at).getTime()
                            : 0
                        break
                    case 'last_step_sent_at':
                        aValue = a.last_step_sent_at
                            ? new Date(a.last_step_sent_at).getTime()
                            : 0
                        bValue = b.last_step_sent_at
                            ? new Date(b.last_step_sent_at).getTime()
                            : 0
                        break
                    case 'next_step_scheduled_at':
                        aValue = a.next_step_scheduled_at
                            ? new Date(a.next_step_scheduled_at).getTime()
                            : 0
                        bValue = b.next_step_scheduled_at
                            ? new Date(b.next_step_scheduled_at).getTime()
                            : 0
                        break
                    default:
                        return 0
                }

                if (aValue < bValue) {
                    return sortDirection === 'asc' ? -1 : 1
                }
                if (aValue > bValue) {
                    return sortDirection === 'asc' ? 1 : -1
                }
                return 0
            })
        }

        return filtered
    }, [prospects, searchQuery, statusFilter, sortField, sortDirection])

    // Paginate the filtered and sorted prospects
    const paginatedProspects = useMemo(() => {
        const startIndex = page * rowsPerPage
        const endIndex = startIndex + rowsPerPage
        return filteredAndSortedProspects.slice(startIndex, endIndex)
    }, [filteredAndSortedProspects, page, rowsPerPage])

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0) // Reset to first page when changing rows per page
    }

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortField(field)
            setSortDirection('asc')
        }
    }

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return null
        return sortDirection === 'asc' ? (
            <ArrowUpwardIcon className={classes.sortIcon} />
        ) : (
            <ArrowDownwardIcon className={classes.sortIcon} />
        )
    }

    if (isLoading) {
        return (
            <Box className={classes.tabContent}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{ minHeight: '400px' }}
                >
                    <CircularProgress />
                </Box>
            </Box>
        )
    }

    if (error && error instanceof Error) {
        return (
            <Box className={classes.tabContent}>
                <Card className={classes.activityCard}>
                    <CardContent>
                        <Typography variant="body1" color="error">
                            Failed to load prospects
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (prospects.length === 0) {
        return (
            <Box className={classes.tabContent}>
                <Card className={classes.activityCard}>
                    <CardContent>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{ padding: 48 }}
                        >
                            <PeopleIcon
                                style={{
                                    fontSize: 48,
                                    color: '#94a3b8',
                                    marginBottom: 16,
                                }}
                            />
                            <Typography variant="h6" gutterBottom>
                                No prospects found
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Prospects will appear here once they are added
                                to this campaign
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    return (
        <Box className={classes.tabContent}>
            {/* Search and Filter Toolbar */}
            <Box className={classes.toolbar}>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    placeholder="Search by prospect name..."
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value)
                        setPage(0) // Reset to first page when search changes
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: '#6b7280' }} />
                            </InputAdornment>
                        ),
                    }}
                    size="small"
                />
                <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.filterControl}
                >
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value as string)
                            setPage(0) // Reset to first page when filter changes
                        }}
                        label="Status"
                    >
                        <MenuItem value="all">All Statuses</MenuItem>
                        {uniqueStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                                {status}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Results count */}
            {(filteredAndSortedProspects.length !== prospects.length ||
                filteredAndSortedProspects.length > rowsPerPage) && (
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: theme.spacing(1) }}
                >
                    Showing {page * rowsPerPage + 1}-
                    {Math.min(
                        (page + 1) * rowsPerPage,
                        filteredAndSortedProspects.length,
                    )}{' '}
                    of {filteredAndSortedProspects.length} prospects
                    {filteredAndSortedProspects.length !== prospects.length &&
                        ` (filtered from ${prospects.length} total)`}
                </Typography>
            )}

            <TableContainer
                component={Paper}
                className={classes.tableContainer}
            >
                <Table
                    className={classes.table}
                    aria-label="prospect tracking table"
                >
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell>
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() => handleSort('name')}
                                >
                                    Prospect
                                    {getSortIcon('name')}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() => handleSort('status')}
                                >
                                    Status
                                    {getSortIcon('status')}
                                </Box>
                            </TableCell>
                            <TableCell>Concession</TableCell>
                            <TableCell>
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() => handleSort('current_step')}
                                >
                                    Current Step
                                    {getSortIcon('current_step')}
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() => handleSort('messages_sent')}
                                    style={{ justifyContent: 'center' }}
                                >
                                    Messages Sent
                                    {getSortIcon('messages_sent')}
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() =>
                                        handleSort('messages_opened')
                                    }
                                    style={{ justifyContent: 'center' }}
                                >
                                    Opened
                                    {getSortIcon('messages_opened')}
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() =>
                                        handleSort('messages_clicked')
                                    }
                                    style={{ justifyContent: 'center' }}
                                >
                                    Clicked
                                    {getSortIcon('messages_clicked')}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() => handleSort('started_at')}
                                >
                                    Started
                                    {getSortIcon('started_at')}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() =>
                                        handleSort('last_step_sent_at')
                                    }
                                >
                                    Last Sent
                                    {getSortIcon('last_step_sent_at')}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box
                                    className={classes.sortableHeader}
                                    onClick={() =>
                                        handleSort('next_step_scheduled_at')
                                    }
                                >
                                    Next Scheduled
                                    {getSortIcon('next_step_scheduled_at')}
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedProspects.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={10}
                                    align="center"
                                    style={{ padding: 48 }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        No prospects match your search criteria
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedProspects.map(
                                (prospect: CampaignProspect) => (
                                    <TableRow
                                        key={prospect.id}
                                        className={classes.tableRow}
                                    >
                                        <TableCell>
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                alignItems="flex-start"
                                                style={{ gap: 6 }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{ fontWeight: 500 }}
                                                >
                                                    {prospect.prospect.name}
                                                </Typography>
                                                <CampaignEmailUnsubscribeIndicator
                                                    prospect={prospect.prospect}
                                                />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={
                                                    prospect.status_display ||
                                                    prospect.status
                                                }
                                                style={{
                                                    backgroundColor:
                                                        getStatusColor(
                                                            prospect.status,
                                                        ) !== 'default' &&
                                                        getStatusColor(
                                                            prospect.status,
                                                        ) !== 'secondary'
                                                            ? getStatusColor(
                                                                  prospect.status,
                                                              )
                                                            : undefined,
                                                    color:
                                                        getStatusColor(
                                                            prospect.status,
                                                        ) !== 'default' &&
                                                        getStatusColor(
                                                            prospect.status,
                                                        ) !== 'secondary'
                                                            ? 'white'
                                                            : undefined,
                                                }}
                                                color={
                                                    getStatusColor(
                                                        prospect.status,
                                                    ) === 'default' ||
                                                    getStatusColor(
                                                        prospect.status,
                                                    ) === 'secondary'
                                                        ? (getStatusColor(
                                                              prospect.status,
                                                          ) as
                                                              | 'default'
                                                              | 'secondary')
                                                        : undefined
                                                }
                                                size="small"
                                                className={classes.statusChip}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {prospect.concession_names?.length >
                                            0 ? (
                                                <Box
                                                    display="flex"
                                                    flexWrap="wrap"
                                                    style={{ gap: 4 }}
                                                >
                                                    {prospect.concession_names.map(
                                                        (
                                                            name: string,
                                                            i: number,
                                                        ) => (
                                                            <Chip
                                                                key={i}
                                                                label={name}
                                                                size="small"
                                                                style={{
                                                                    backgroundColor:
                                                                        '#d1fae5',
                                                                    color:
                                                                        '#047857',
                                                                    fontWeight: 600,
                                                                    fontSize:
                                                                        '0.625rem',
                                                                    height: 22,
                                                                    borderRadius: 6,
                                                                }}
                                                            />
                                                        ),
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {prospect.current_step
                                                    ? `Step ${prospect.current_step.step_order} (${prospect.current_step.step_type})`
                                                    : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                style={{ fontWeight: 500 }}
                                            >
                                                {prospect.messages_sent}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color: '#8b5cf6',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {prospect.messages_opened}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    color: '#10b981',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {prospect.messages_clicked}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(
                                                    prospect.started_at,
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(
                                                    prospect.last_step_sent_at,
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(
                                                    prospect.next_step_scheduled_at,
                                                )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ),
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {filteredAndSortedProspects.length > 0 && (
                <TablePagination
                    component="div"
                    count={filteredAndSortedProspects.length}
                    page={page}
                    onChangePage={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    style={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: '#ffffff',
                    }}
                />
            )}
        </Box>
    )
}
